import { useMemo } from "react";

import { sameName, upper } from "../utils/teacher/InputRatingUtils";

const useSharePermissions = ({
    student,
    studentProfileData,
    selectedTemplate,
    view,
    userDetails,
    gradingProfiles,
    selectedGroupContent,
    groupPermissionField = "groupPermission",
    currentTeacherName = null,
    navMode,
    incomingTemplateId
}) => {
    return useMemo(() => {
        if (Number(userDetails?.uid) === Number(studentProfileData?.teacherGuid)) {
            return { canEdit: true, isOwned: true };
        }

        if (!student || (!selectedTemplate && navMode !== "GROUPS_SLE" && incomingTemplateId !== "SLE")) {
            return { canEdit: false, isOwned: false };
        }

        // ----- Homeroom case -----
        if (view === "HOMEROOM") {
            const isInMyHomeroom = gradingProfiles?.some((gp) => String(gp.key) === String(student.gradingProfile));
            return { canEdit: isInMyHomeroom, isOwned: isInMyHomeroom };
        }

        // ----- Groups case -----
        let rows = Array.isArray(selectedGroupContent) ? selectedGroupContent : [];
        if (rows.length && Array.isArray(rows[0]?.groupStudentContents)) {
            rows = rows.flatMap((g) =>
                (g.groupStudentContents || []).map((s) => ({
                    ...s,
                    groupId: g.groupId ?? s.groupId,
                    groupType: g.groupType ?? s.groupType,
                    teacherName: g.teacherName ?? s.teacherName,
                    [groupPermissionField]: s[groupPermissionField] ?? g[groupPermissionField],
                    sharedMeta: g.sharedMeta ?? s.sharedMeta
                }))
            );
        }

        const forStudent = rows.filter((r) => String(r.studentNumber) === String(student.studentNumber));

        let hasEdit = false;
        const sourceGroups = forStudent.map((r) => {
            const perm = upper(
                r[groupPermissionField] ?? r.groupPermission ?? r.permission ?? r.sharedMeta?.sharedPermission
            );

            if (perm === "EDIT") hasEdit = true;
            return {
                groupType: r.groupType ?? (r.sharedMeta ? "SHARED" : undefined),
                teacherName: r.teacherName ?? r.sharedMeta?.teacherName
            };
        });

        // fallback if no group match
        if (!forStudent.length) {
            const fallbackPerm = upper(student[groupPermissionField] ?? student.groupPermission ?? student.permission);
            if (fallbackPerm === "EDIT") hasEdit = true;
        }

        // ownership logic
        let isOwned = false;
        if (sourceGroups.some((g) => upper(g.groupType) === "HOMEROOM")) {
            isOwned = true;
        } else if (sourceGroups.length && sourceGroups.some((g) => sameName(g.teacherName, currentTeacherName))) {
            isOwned = true;
        }

        const canEdit = isOwned || hasEdit;

        return { canEdit, isOwned };
    }, [
        student,
        studentProfileData,
        selectedTemplate,
        userDetails,
        view,
        gradingProfiles,
        selectedGroupContent,
        groupPermissionField,
        currentTeacherName,
        navMode,
        incomingTemplateId
    ]);
};

export default useSharePermissions;
